import { addActionBarItem, ActionBarContext, DataService, NotificationService } from '@vendure/admin-ui/core';
import { Injectable } from '@angular/core';
import { gql } from 'graphql-tag';
import { Observable, from, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { Router } from '@angular/router';

interface DoorDashStore {
    doorDashStore: {
        status: string;
        message: string;
    };
}

interface DoorDashDelivery {
    cancelDoordashDelivery: {
        status: string;
        message: string;
    };
}

@Injectable()
class MyService {
    constructor(private readonly dataService: DataService) {}

    doordashStoreState(channelId: string): Observable<boolean> {
        const query = gql`
            query DoorDashStoreState($channelId: ID!) {
                doorDashStoreState(id: $channelId)
            }
        `;

        return this.dataService
            .query<{ doorDashStoreState: boolean }>(query, { channelId })
            .mapSingle(response => response.doorDashStoreState)
            .pipe(
                catchError(() => {
                    return of(false);
                }),
            );
    }

    createDoorDashStore(channelId: string): Observable<DoorDashStore> {
        const mutation = gql`
            mutation CreateDoorDashStore($channelId: ID!) {
                createDoordashStore(id: $channelId) {
                    status
                    message
                }
            }
        `;
        return this.dataService.mutate<DoorDashStore>(mutation, { channelId }).pipe(
            catchError(error => {
                throw error;
            }),
        );
    }

    cancelDelivery(ids: [string]): Observable<DoorDashDelivery> {
        const mutation = gql`
            mutation cancelDoordashDelivery($ids: [ID!]!) {
                cancelDoordashDelivery(ids: $ids) {
                    status
                    message
                }
            }
        `;
        return this.dataService
            .mutate<DoorDashDelivery>(mutation, {
                ids,
            })
            .pipe(
                catchError(error => {
                    throw error;
                }),
            );
    }

    doordashDeliveryCancelState(orderId: number): Observable<boolean> {
        const query = gql`
            query DoordashDeliveryCancelState($orderId: ID!) {
                doordashDeliveryCancelState(id: $orderId)
            }
        `;

        return this.dataService
            .query<{ doordashDeliveryCancelState: boolean }>(query, { orderId })
            .mapSingle(response => response.doordashDeliveryCancelState)
            .pipe(
                catchError(() => {
                    return of(false);
                }),
            );
    }
}

export default [
    MyService,

    addActionBarItem({
        id: 'cancel-doordash-delivery',
        locationId: 'order-detail',
        label: 'Cancel Doordash Delivery',
        icon: 'times',
        requiresPermission: 'UpdateOrder',
        buttonColor: 'warning',
        buttonState: (context: ActionBarContext) => {
            const orderId = context.route.snapshot.params.id;
            const myService = context.injector.get(MyService);
            return myService.doordashDeliveryCancelState(orderId).pipe(
                map(state => ({
                    disabled: !state,
                    visible: state,
                })),
            );
        },
        onClick: async (event: MouseEvent, context: ActionBarContext) => {
            const orderId = context.route.snapshot.params.id;
            const myService = context.injector.get(MyService);
            const notificationService = context.injector.get(NotificationService);
            myService.cancelDelivery([orderId]).subscribe({
                next: response => {
                    const { status, message } = response.cancelDoordashDelivery;
                    if (status === 'Success') {
                        notificationService.success('Doordash delivery canceled successfully.');
                    } else {
                        notificationService.error(`Error: ${message}`);
                    }
                },
                error: () => {
                    notificationService.error('Error occurred canceling the Doordash delivery.');
                },
            });
        },
    }),

    addActionBarItem({
        id: 'create-doordash-store',
        locationId: 'channel-detail',
        label: 'Create Doordash Store',
        icon: 'check',
        requiresPermission: 'UpdateChannel',
        buttonColor: 'success',
        onClick: async (event: MouseEvent, context: ActionBarContext) => {
            const channelId: string = context.route.snapshot.params.id;
            const myService = context.injector.get(MyService);
            const notificationService = context.injector.get(NotificationService);

            const isAuthorized = await myService.doordashStoreState(channelId).toPromise();
            if (isAuthorized) {
                notificationService.error('The DoorDash store is already authorized.');
                return;
            }

            myService.createDoorDashStore(channelId).subscribe({
                next: response => {
                    const { status, message } = response.doorDashStore;
                    if (status === 'Success') {
                        notificationService.success('Doordash store created successfully.');
                    } else {
                        notificationService.error(`Error: ${message}`);
                    }
                },
                error: () => {
                    notificationService.error('Error occurred creating the DoorDash store.');
                },
            });
        },
    }),
];
